import React, { useEffect, useRef, useState } from 'react';
import { Box, Container, Typography, Chip, Button, Stack } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const plans = [
  {
    name: 'Starter',
    price: '29',
    period: '/mo',
    desc: 'Perfect for small fleets getting started with tracking.',
    features: ['Up to 10 vehicles', 'Real-time GPS tracking', 'Basic geofencing (3 zones)', 'Email alerts', 'Driver app access', '7-day history'],
    cta: 'Book a Demo',
    highlight: false,
  },
  {
    name: 'Professional',
    price: '79',
    period: '/mo',
    desc: 'For growing fleets that need advanced insights and controls.',
    features: ['Up to 50 vehicles', 'Everything in Starter', 'Unlimited geofences', 'Driver behavior scoring', 'Issue reporting & photos', '90-day history', 'SMS + push alerts', 'API access'],
    cta: 'Book a Demo',
    highlight: true,
    badge: 'Most Popular',
  },
  {
    name: 'Enterprise',
    price: '199',
    period: '/mo',
    desc: 'Custom solutions for large-scale fleet operations.',
    features: ['Unlimited vehicles', 'Everything in Pro', 'White-label option', 'Dedicated account manager', 'Custom integrations', '1-year history', 'SLA guarantee', 'On-premise option'],
    cta: 'Contact Sales',
    highlight: false,
  },
];

function PricingCard({ plan, index }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.15 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <Box
      ref={ref}
      sx={{
        borderRadius: '24px',
        p: 4,
        height: '100%',
        position: 'relative',
        opacity: visible ? 1 : 0,
        transform: visible ? (plan.highlight ? 'scale(1.04)' : 'none') : 'translateY(30px)',
        transition: `all 0.65s ${index * 0.15}s ease`,
        ...(plan.highlight ? {
          background: 'linear-gradient(145deg, #1075BA 0%, #0a5a96 100%)',
          boxShadow: '0 30px 70px rgba(16,117,186,0.4)',
          border: 'none',
          zIndex: 1,
        } : {
          background: '#fff',
          border: '1px solid rgba(16,117,186,0.12)',
          boxShadow: '0 8px 30px rgba(16,117,186,0.08)',
          '&:hover': { boxShadow: '0 20px 50px rgba(16,117,186,0.15)', transform: 'translateY(-4px)' },
        }),
      }}
    >
      {plan.badge && (
        <Chip
          label={plan.badge}
          size="small"
          sx={{
            position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
            bgcolor: '#F8B220', color: '#0d1f35', fontWeight: 800, fontSize: '0.75rem',
            boxShadow: '0 4px 15px rgba(248,178,32,0.4)',
          }}
        />
      )}

      <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: plan.highlight ? 'rgba(255,255,255,0.7)' : '#1075BA', mb: 1 }}>
        {plan.name}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, mb: 1 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '3rem', color: plan.highlight ? '#fff' : '#0d1f35', lineHeight: 1 }}>
          ${plan.price}
        </Typography>
        <Typography sx={{ color: plan.highlight ? 'rgba(255,255,255,0.6)' : '#4a6580', mb: 0.8, fontWeight: 600 }}>
          {plan.period}
        </Typography>
      </Box>

      <Typography sx={{ color: plan.highlight ? 'rgba(255,255,255,0.75)' : '#4a6580', mb: 3.5, fontSize: '0.88rem', lineHeight: 1.6 }}>
        {plan.desc}
      </Typography>

      <Stack gap={1.5} sx={{ mb: 4 }}>
        {plan.features.map((f) => (
          <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CheckCircleIcon sx={{ fontSize: 18, color: plan.highlight ? '#F8B220' : '#1075BA', flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.88rem', color: plan.highlight ? 'rgba(255,255,255,0.9)' : '#4a6580', fontWeight: 500 }}>
              {f}
            </Typography>
          </Box>
        ))}
      </Stack>

      <Button
        fullWidth
        variant={plan.highlight ? 'contained' : 'outlined'}
        sx={plan.highlight ? {
          bgcolor: '#F8B220', color: '#0d1f35',
          '&:hover': { bgcolor: '#ffc94d', boxShadow: '0 8px 24px rgba(248,178,32,0.4)' },
          fontWeight: 700, py: 1.5,
        } : {
          borderColor: 'rgba(16,117,186,0.3)', color: '#1075BA',
          '&:hover': { borderColor: '#1075BA', bgcolor: 'rgba(16,117,186,0.04)' },
          py: 1.5,
        }}
      >
        {plan.cta}
      </Button>
    </Box>
  );
}

export default function Pricing() {
  const [titleVisible, setTitleVisible] = useState(false);
  const titleRef = useRef();

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setTitleVisible(true); obs.disconnect(); } }, { threshold: 0.3 });
    if (titleRef.current) obs.observe(titleRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <Box id="pricing" sx={{ py: { xs: 10, md: 14 }, bgcolor: '#fff', position: 'relative', overflow: 'hidden' }}>
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1, background: 'linear-gradient(90deg, transparent, rgba(16,117,186,0.15), transparent)' }} />
      <Box sx={{ position: 'absolute', bottom: -200, right: -200, width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(248,178,32,0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />

      <Container maxWidth="lg">
        <Box ref={titleRef} sx={{ textAlign: 'center', mb: 8, opacity: titleVisible ? 1 : 0, transform: titleVisible ? 'none' : 'translateY(20px)', transition: 'all 0.7s ease' }}>
          <Chip label="Pricing" sx={{ mb: 2.5, bgcolor: 'rgba(16,117,186,0.08)', color: '#1075BA', fontWeight: 700, border: '1px solid rgba(16,117,186,0.2)' }} />
          <Typography variant="h2" sx={{ fontSize: { xs: '2.2rem', md: '3rem' }, color: '#0d1f35', mb: 2 }}>
            Simple,{' '}
            <Box component="span" sx={{ background: 'linear-gradient(135deg, #1075BA, #3a9de4)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Transparent
            </Box>
            {' '}Pricing
          </Typography>
          <Typography sx={{ color: '#4a6580', fontSize: '1.05rem' }}>
            No hidden fees. Cancel anytime. Book a demo to get started.
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3, alignItems: 'center' }}>
          {plans.map((plan, i) => (
            <PricingCard key={plan.name} plan={plan} index={i} />
          ))}
        </Box>

        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Typography sx={{ color: '#4a6580', fontSize: '0.9rem' }}>
            Ready to get started? <Box component="span" sx={{ fontWeight: 700, color: '#1075BA' }}>Book a demo</Box> and we'll walk you through the right plan for your fleet.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
